import { AfterViewInit, Component, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { DataService } from '../../../services/data/data.service';
import { TranslateService } from '@ngx-translate/core';
import { FiltersM, typesApex } from '../../../components/models';
import { GraficadorComponent } from '../../../components/graficador/graficador.component';

/**
 * Estructura de cada sección/subsección del informe.
 * - `encabezado`: la fila es solo un título de grupo (sin toggle propio).
 * - `destacado`: sección principal (texto en negrita).
 * - `hijos`: subsecciones (sucursales, variables del análisis combinado).
 * - `texto`: análisis editable que el usuario puede ajustar antes de exportar.
 */
interface SeccionInforme {
  id: string;
  nombre: string;
  activo: boolean;
  destacado?: boolean;
  encabezado?: boolean;
  hijos?: SeccionInforme[];
  texto?: string;
}

/**
 * Informe de clima laboral (constructor / vista previa del reporte).
 *
 *
 *  1. `secciones[]`  → catálogo único que alimenta el panel de toggles
 *                      (qué entra al informe) y la vista previa.
 *  2. Vista previa   → una `<section>` por sección, con su gráfica
 *                      (`app-graficador` + `zona` id), tabla y `textarea`
 *                      editable (igual que `reporte`).
 *  3. `generarPDF()` → orquestador. Por cada sección activa agrega su bloque
 *                      al `content`, llamando a un método `*Text()`.
 *  4. `*Text()`      → STUBS (uno por sección). Aquí va la construcción real
 *                      de los nodos pdfMake + exportación de gráficas. Buscar
 *                      los `// TODO` y sustituir el mock por el servicio GraphQL.
 */
@Component({
  selector: 'app-informe',
  templateUrl: './informe.component.html',
  styleUrl: './informe.component.css'
})
export class InformeComponent implements AfterViewInit {

  /** Referencia a todas las gráficas de la preview (para exportar su imagen al PDF) */
  @ViewChildren(GraficadorComponent) grafCmp!: QueryList<GraficadorComponent>;

  // -------------------- CATÁLOGOS GRÁFICA (mock) --------------------
  public types = typesApex;
  public colorScheme = ['#2843A0', '#15A075', '#36CAE8', '#F88476', '#E23E6A', '#FFB200', '#78EAD2'];
  public legendGraf = { enabled: true, position: 'bottom' };
  /** Dimensiones evaluadas (mock) */
  public dimLabels = ['Liderazgo', 'Comunicación', 'Trabajo en equipo', 'Reconocimiento', 'Ambiente laboral'];
  /** Serie global por dimensión (mock) */
  public globalData = [{ name: 'Calificación', data: [4.1, 3.9, 4.3, 3.7, 4.0] }];
  /** Serie para la gráfica de dimensiones (mock) */
  public dimensionesData = [{ name: 'Global', data: [4.1, 3.9, 4.3, 3.7, 4.0] }];
  /** Tabla de composición de la muestra (mock) */
  public composicionTabla = [
    { caracteristica: 'Total de colaboradores', valor: '320' },
    { caracteristica: 'Colaboradores encuestados', valor: '295' },
    { caracteristica: 'Participación', valor: '92.2 %' },
    { caracteristica: 'Sucursales evaluadas', valor: '7' },
  ];

  // -------------------- FILTROS AÑO / MES (mock) --------------------
  /** Enum de tipos de filtro del componente compartido app-filters */
  public Filtros = FiltersM;

  /** Años disponibles (mock) */
  public aniosDisponibles: any[] = [
    { name: '2024', anio: 2024, value: 2024, check: false },
    { name: '2025', anio: 2025, value: 2025, check: true },
  ];
  /** Meses del año seleccionado (mock) */
  public mesesDisponibles: any[] = [
    { name: 'Marzo', value: 202503, year: 2025, check: false },
    { name: 'Junio', value: 202506, year: 2025, check: false },
    { name: 'Septiembre', value: 202509, year: 2025, check: false },
    { name: 'Diciembre', value: 202512, year: 2025, check: true },
  ];

  /** Etiquetas de la selección actual (para la vista previa) */
  public anioSel = '2025';
  public mesSel = 'Diciembre';

  // -------------------- SECCIONES DEL INFORME (mock / catálogo) --------------------
  public secciones: SeccionInforme[] = [
    {
      id: 'metodologia', nombre: 'Metodología', activo: true, destacado: true,
      texto: 'La evaluación de clima laboral se aplicó mediante un cuestionario estructurado por dimensiones, ' +
        'con una escala de calificación de 1 a 5. El levantamiento se realizó de forma anónima.'
    },
    {
      id: 'composicion', nombre: 'Composición de la muestra', activo: true, destacado: true,
      texto: 'La muestra evaluada se compone de los colaboradores que participaron en el levantamiento, ' +
        'distribuidos por sucursal, área y puesto.'
    },
    {
      id: 'evaluacion-global', nombre: 'Evaluación global', activo: true, destacado: true,
      texto: 'El resultado global por dimensión refleja la percepción general del clima laboral en el período evaluado.'
    },
    {
      id: 'consolidado', nombre: 'Consolidado', activo: true, destacado: true,
      texto: 'Resultado consolidado de la institución por dimensión.',
      hijos: [
        { id: 'suc1', nombre: 'Sucursal 1', activo: false, texto: 'Resultado de la Sucursal 1.' },
        { id: 'suc2', nombre: 'Sucursal 2', activo: false, texto: 'Resultado de la Sucursal 2.' },
        { id: 'suc3', nombre: 'Sucursal 3', activo: false, texto: 'Resultado de la Sucursal 3.' },
      ]
    },
    {
      id: 'dimensiones', nombre: 'Evaluación dimensiones', activo: true, destacado: true,
      texto: 'Detalle de la calificación obtenida en cada una de las dimensiones evaluadas.'
    },
    {
      id: 'analisis-combinado', nombre: 'Análisis combinado', activo: true, encabezado: true,
      hijos: [
        { id: 'ac-sucursales', nombre: 'Sucursales', activo: true, texto: 'Análisis combinado por sucursal.' },
        { id: 'ac-rango-edad', nombre: 'Rango de edad', activo: false, texto: 'Análisis combinado por rango de edad.' },
        { id: 'ac-antiguedad', nombre: 'Antiguedad', activo: true, texto: 'Análisis combinado por antigüedad.' },
      ]
    },
    {
      id: 'conclusiones', nombre: 'Conclusiones', activo: true, destacado: true,
      texto: 'Conclusiones generales de la evaluación de clima laboral del período.'
    },
  ];

  /** Path del icono "+" para el thumb del toggle encendido (mismo que en reporte) */
  private onSVG = 'M12 13 4 13 4 11 12 11 12 3 14 3 14 11 22 11V13L14 13 14 21H12L12 13Z';

  constructor(
    public dataService: DataService,
    public translate: TranslateService,
    private host: ElementRef,
  ) { }

  ngAfterViewInit(): void {
    this.repintarIconos();
  }

  // ============================================================
  //  PANEL DE TOGGLES
  // ============================================================
  /**
   * Cambia el icono del estado encendido de los toggles a un "+".
   * Se vuelve a aplicar tras cada cambio porque al colapsar/expandir
   * subsecciones Angular recrea los nodos del switch.
   */
  public repintarIconos(): void {
    setTimeout(() => {
      const iconos = this.host.nativeElement.querySelectorAll('.mdc-switch__icon--on');
      iconos.forEach((ic: any) => {
        if (ic.firstChild) {
          ic.firstChild.setAttribute('d', this.onSVG);
        }
      });
    });
  }

  // ============================================================
  //  FILTROS
  // ============================================================
  /** Selección de año (radio: solo uno activo) */
  public anioChange(anio: any): void {
    this.aniosDisponibles.forEach(a => a.check = false);
    anio.check = true;
    this.anioSel = anio.name;
    // (mock) aquí se recargarían los meses del año seleccionado desde el backend
  }

  /** Selección de mes (radio: solo uno activo) */
  public mesChange(mes: any): void {
    this.mesesDisponibles.forEach(m => m.check = false);
    mes.check = true;
    this.mesSel = mes.name;
  }

  // ============================================================
  //  ACCESO AL CATÁLOGO DE SECCIONES
  // ============================================================
  /** Devuelve la sección por id */
  public seccion(id: string): SeccionInforme {
    return this.secciones.find(s => s.id === id)!;
  }
  /** ¿La sección está activa? */
  public activa(id: string): boolean {
    return !!this.seccion(id)?.activo;
  }
  /** ¿La sección tiene al menos una subsección activa? */
  public tieneHijosActivos(sec: SeccionInforme): boolean {
    return !!sec?.hijos?.some(h => h.activo);
  }

  // ============================================================
  //  GENERACIÓN DEL INFORME 
  // ============================================================
  /**
   * Orquestador. Por cada sección activa agrega su bloque al `content`.
   * Misma mecánica que `riesgo-credito/reporte.generarPDF()`.
   */
  public async generarPDF(): Promise<void> {
    const metodologia = this.activa('metodologia') ? await this.metodologiaText() : [];
    const composicion = this.activa('composicion') ? await this.composicionText() : [];
    const evaluacionGlobal = this.activa('evaluacion-global') ? await this.evaluacionGlobalText() : [];
    const consolidado = this.activa('consolidado') ? await this.consolidadoText() : [];
    const dimensiones = this.activa('dimensiones') ? await this.dimensionesText() : [];
    const analisis = this.tieneHijosActivos(this.seccion('analisis-combinado'))
      ? await this.analisisCombinadoText() : [];
    const conclusiones = this.activa('conclusiones') ? await this.conclusionesText() : [];

    const docPDF = {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 60],
      content: [
        this.portada(),
        { text: '', pageBreak: 'after' },
        this.introductionText(),
        metodologia,
        composicion,
        evaluacionGlobal,
        consolidado,
        dimensiones,
        analisis,
        conclusiones,
      ],
    };

    // TODO (dev funcionalidad): generar el PDF con pdfMake, igual que en
    // riesgo-credito/reporte:
    //   const pdf = pdfMake.createPdf(docPDF);
    //   await pdf.download('Informe clima laboral');
    console.log('docPDF (mock):', docPDF);
  }

  /** Portada del informe (STUB) */
  private portada(): any[] {
    // TODO: portada real (logo institución, nombre, período).
    return [
      { text: 'INFORME DE EVALUACIÓN', bold: true, fontSize: 28, margin: [60, 50, 0, 0] },
      { text: 'Clima laboral', bold: true, fontSize: 18, color: '#949393', margin: [60, 45, 0, 0] },
      { text: `Evaluación ${this.mesSel} ${this.anioSel}`, fontSize: 18, margin: [60, 100, 0, 0] },
    ];
  }

  /** Introducción del informe (STUB) */
  private introductionText(): any[] {
    return this.bloqueTextoPDF('Introducción', 'TODO: introducción del informe de clima laboral.');
  }

  // ---- Un método por sección (STUBS). Aquí va la construcción real. ----

  private async metodologiaText(): Promise<any[]> {
    // TODO: nodos pdfMake de la metodología.
    return this.bloqueTextoPDF(this.seccion('metodologia').nombre, this.seccion('metodologia').texto);
  }

  private async composicionText(): Promise<any[]> {
    // TODO: agregar la tabla de composición (this.composicionTabla) en formato pdfMake.
    return this.bloqueTextoPDF(this.seccion('composicion').nombre, this.seccion('composicion').texto);
  }

  private async evaluacionGlobalText(): Promise<any[]> {
    // TODO: exportar la imagen de la gráfica e insertarla:
    //   const img = await this.grafCmp.find(g => g.zona === 1)?.exportImgBlob();
    return this.bloqueTextoPDF(this.seccion('evaluacion-global').nombre, this.seccion('evaluacion-global').texto);
  }

  private async consolidadoText(): Promise<any[]> {
    const sec = this.seccion('consolidado');
    const bloques = this.bloqueTextoPDF(sec.nombre, sec.texto);
    // TODO: por cada sucursal activa, agregar su gráfica/tabla y su análisis.
    sec.hijos?.filter(h => h.activo).forEach(h => bloques.push(...this.bloqueTextoPDF(h.nombre, h.texto)));
    return bloques;
  }

  private async dimensionesText(): Promise<any[]> {
    // TODO: exportar la gráfica de dimensiones (zona 2).
    return this.bloqueTextoPDF(this.seccion('dimensiones').nombre, this.seccion('dimensiones').texto);
  }

  private async analisisCombinadoText(): Promise<any[]> {
    const sec = this.seccion('analisis-combinado');
    const bloques: any[] = [{ text: sec.nombre, bold: true, fontSize: 14, margin: [0, 30, 0, 0] }];
    // TODO: por cada variable activa, exportar su gráfica (zona 10+i) y su análisis.
    sec.hijos?.filter(h => h.activo).forEach(h => bloques.push(...this.bloqueTextoPDF(h.nombre, h.texto)));
    return bloques;
  }

  private async conclusionesText(): Promise<any[]> {
    return this.bloqueTextoPDF(this.seccion('conclusiones').nombre, this.seccion('conclusiones').texto);
  }

  /**
   * Helper que arma un bloque pdfMake básico (título + línea + texto).
   * Mismma forma que los bloques de `reporte`. El dev lo ampliará con
   * tablas/gráficas según la sección.
   */
  private bloqueTextoPDF(titulo: string, texto: string = ''): any[] {
    return [
      { text: titulo, bold: true, fontSize: 14, margin: [0, 30, 0, 0] },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 }], margin: [0, 5, 0, 20] },
      { text: texto ?? '', alignment: 'justify', fontSize: 11 },
    ];
  }
}
